import { z } from "zod";
import { filterDefinitionSchema } from "./filters.js";
import { LEAD_SORT_FIELDS } from "./leads.js";
import { actorViewSchema } from "./people.js";
import { idSchema, singleLineTextSchema } from "./text.js";
// Smart List dinamiche (piano M3, Task 7; D7–D8; `CRM_DATA_DICTIONARY.md` §8.1–8.2). Una lista è una definizione dei
// filtri con un ordinamento, mai un elenco di Lead: la sua pagina legge `GET /api/leads?filter=…&who=all` con quella
// definizione, nel perimetro di chi guarda. «Di chi» non si salva (`PRODUCT_SPEC.md` §8: «Owner = Me» è soltanto un
// filtro, decisione P5), quindi il numero del menu e quello della pagina contano la stessa condizione con `who = all`.
// L'accesso alla lista è l'area `smart_lists` dei permessi; ogni creazione, salvataggio, condivisione e rinomina scrive
// una revisione con le differenze (`changes`), che lo «Storico della lista» rende in parole semplici.
/** «Chi la vede»: «Privata», «Condivisa con colleghi scelti», «Pubblica al team». */
export const SMART_LIST_VISIBILITIES = ["private", "shared", "public_team"];
/** I gruppi del menu: «Tue» · «Condivise con te» · «Del team» · «Dei colleghi» (soltanto con la lettura `all`). */
export const SMART_LIST_GROUPS = ["mine", "shared_with_me", "team", "colleagues"];
/**
 * I gruppi che una persona ordina nel suo menu (SL-11, richiesta del product owner del 23 ottobre 2026): «Fissate» in cima,
 * poi quelli di `SMART_LIST_GROUPS`. Una lista fissata sta soltanto in «Fissate»; togliendo la puntina torna nel suo gruppo.
 */
export const SMART_LIST_MENU_GROUPS = ["pinned", ...SMART_LIST_GROUPS];
export const smartListSortSchema = z.strictObject({ field: z.enum(LEAD_SORT_FIELDS), direction: z.enum(["asc", "desc"]) });
/** Una persona della lista (owner o destinatario): il nome di adesso e se la sua appartenenza è attiva. */
const personSchema = z.object({ membershipId: z.string(), displayName: z.string(), active: z.boolean() });
/**
 * `count`: i Lead della lista nel perimetro di chi guarda, con `who = all`; `null` se il conteggio di quella lista non è
 * riuscito (il menu risponde lo stesso). `newCount`, le «novità» (riscontro del product owner del 3 ottobre 2026, questione
 * aperta 182): fra quei Lead, quelli arrivati in Gioia365 (`created_at`) dopo l'ultima volta che chi guarda ha aperto la
 * lista (`POST /api/smart-lists/:id/opened`), oppure, se non l'ha mai aperta, dopo la nascita della lista; nella Vista
 * Commerciale quelli della persona osservata; per il Super admin di Gioia365 nel contesto di piattaforma le sue, per la sua
 * identità (migrazione 0011); `null` insieme a `count`. `pinned` e `position` sono le preferenze di chi guarda (SL-11,
 * migrazione 0044), anche nella Vista Commerciale, dove il menu è suo: `pinned` se la lista sta in «Fissate», `position` il
 * suo posto nel gruppo `group` (`null`: mai spostata in quel gruppo, dopo le altre per nome).
 */
export const smartListSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    visibility: z.enum(SMART_LIST_VISIBILITIES),
    owner: personSchema,
    group: z.enum(SMART_LIST_GROUPS),
    count: z.number().int().nullable(),
    newCount: z.number().int().nullable(),
    canEdit: z.boolean(),
    revision: z.number().int(),
    pinned: z.boolean(),
    position: z.number().int().min(0).nullable(),
});
/**
 * `GET /api/smart-lists`: le liste che chi guarda vede, prima le fissate nel suo ordine, poi per gruppo (nell'ordine di
 * `SMART_LIST_GROUPS`) e dentro ogni gruppo nel suo ordine, con quelle mai spostate dopo, per nome; al massimo 100, ognuna
 * con il suo conteggio, e `truncated` se ce ne sono altre. Il taglio tocca prima «Dei colleghi», mai le fissate.
 */
export const smartListsResponseSchema = z.object({ lists: z.array(smartListSummarySchema), truncated: z.boolean() });
/** Quante liste dice al massimo un ordine del menu: quante il menu ne mostra (`GET /api/smart-lists`). */
export const SMART_LIST_ORDER_MAX = 100;
/**
 * Corpo di `PUT /api/smart-lists/order` (SL-11): il nuovo ordine di un gruppo del menu di chi guarda, tutto intero, dall'alto.
 * Con `pinned` le liste indicate sono le fissate, in quest'ordine, e le altre fissate tornano nel loro gruppo (così si fissa e
 * si toglie la puntina); con un altro gruppo cambia soltanto il posto delle liste indicate che oggi stanno in quel gruppo, mai
 * la puntina. Le liste che chi guarda non vede (più) si ignorano. Vale per la sola persona che lo manda; l'ultima scrittura vince.
 */
export const saveSmartListOrderBodySchema = z.strictObject({
    group: z.enum(SMART_LIST_MENU_GROUPS),
    ids: z
        .array(idSchema)
        .max(SMART_LIST_ORDER_MAX)
        .refine((ids) => new Set(ids).size === ids.length, { message: "ids must not repeat" }),
});
/** Nessun `restored_revision`: «Rimetti questa versione» è una bozza, e il «Salva» dopo scrive le differenze normali. «Elimina la lista» non scrive una versione. */
export const SMART_LIST_CHANGE_KINDS = [
    "filter_added",
    "filter_changed",
    "filter_removed",
    "sort_changed",
    "name_changed",
    "visibility_changed",
    "recipients_changed",
    "owner_changed",
    "created",
];
/**
 * Una differenza fra due versioni. `field` è il campo del filtro (`status`, `field:<chiave>`) delle differenze dei
 * filtri; senza `field` una `filter_changed` dice che è cambiata la struttura (combinatore o gruppi), che l'interfaccia
 * di M3 non costruisce. `before` e `after` sono i valori di allora: condizioni, ordinamenti, nomi, visibilità, persone
 * (`{ membershipId, displayName }`).
 */
export const smartListChangeSchema = z.object({ kind: z.enum(SMART_LIST_CHANGE_KINDS), field: z.string().optional(), before: z.unknown().optional(), after: z.unknown().optional() });
export const smartListDetailSchema = z.object({
    id: z.string(),
    name: z.string(),
    visibility: z.enum(SMART_LIST_VISIBILITIES),
    owner: personSchema,
    createdBy: actorViewSchema,
    createdAt: z.string(),
    recipients: z.array(personSchema),
    definition: filterDefinitionSchema,
    sort: smartListSortSchema,
    /** Quante condizioni salvate sono state tolte in lettura perché il loro campo non c'è più (`definition` arriva già senza di loro). */
    staleConditions: z.number().int().min(0),
    revision: z.number().int(),
    savedAt: z.string(),
    savedBy: actorViewSchema,
    permissions: z.object({ canSave: z.boolean(), canShare: z.boolean(), canRename: z.boolean(), canDelete: z.boolean(), canCreate: z.boolean() }),
});
/** Corpo di `POST /api/smart-lists` (anche «Salva come nuova»). `ownerMembershipId`: «I Lead di un Commerciale, per lui», con `update: all`. */
export const createSmartListBodySchema = z.strictObject({
    name: singleLineTextSchema(120),
    visibility: z.enum(SMART_LIST_VISIBILITIES),
    recipientIds: z.array(idSchema).max(200).default([]),
    definition: filterDefinitionSchema,
    sort: smartListSortSchema,
    ownerMembershipId: idSchema.optional(),
});
/** Corpo di `PUT /api/smart-lists/:id`, «Salva»: filtri e ordinamento, dalla revisione letta (409 se non è l'ultima). */
export const saveSmartListBodySchema = z.strictObject({ expectedRevision: z.number().int().min(1), definition: filterDefinitionSchema, sort: smartListSortSchema });
/** Corpo di `POST /api/smart-lists/:id/share`, «Condividi»: chi la vede e, con `update: all`, l'owner. */
export const shareSmartListBodySchema = z.strictObject({
    expectedRevision: z.number().int().min(1),
    visibility: z.enum(SMART_LIST_VISIBILITIES),
    recipientIds: z.array(idSchema).max(200).default([]),
    ownerMembershipId: idSchema.optional(),
});
/** Corpo di `POST /api/smart-lists/:id/rename`, «Rinomina». */
export const renameSmartListBodySchema = z.strictObject({ expectedRevision: z.number().int().min(1), name: singleLineTextSchema(120) });
/**
 * Una versione dello storico. `definition` arriva già senza le condizioni che il catalogo dell'azienda non ammette più,
 * come nel dettaglio, così «Rimetti questa versione» si può sempre mandare in `GET /api/leads?filter=`; `staleConditions`
 * dice quante ne sono state tolte. `changes` restano quelle di allora.
 */
export const smartListRevisionSchema = z.object({
    revision: z.number().int(),
    savedAt: z.string(),
    savedBy: actorViewSchema,
    changes: z.array(smartListChangeSchema),
    definition: filterDefinitionSchema,
    staleConditions: z.number().int().min(0),
    sort: smartListSortSchema,
    name: z.string(),
    visibility: z.enum(SMART_LIST_VISIBILITIES),
});
/** Quante versioni dà lo «Storico della lista» al massimo, dalla più recente: il suo pulsante non ne promette di più. */
export const SMART_LIST_REVISIONS_MAX = 100;
/** `GET /api/smart-lists/:id/revisions`: le ultime 100 (`SMART_LIST_REVISIONS_MAX`), dalla più recente. */
export const smartListRevisionsResponseSchema = z.object({ revisions: z.array(smartListRevisionSchema) });
/** La risposta di creazione, salvataggio, condivisione e rinomina: la lista com'è dopo la scrittura. */
export const smartListWriteResponseSchema = z.object({ list: smartListDetailSchema });
//# sourceMappingURL=smart-lists.js.map